import { Box, Heading, HStack } from '@chakra-ui/layout'
import {
  Button,
  Link,
  Stat,
  StatGroup,
  StatHelpText,
  StatNumber,
  Text as ChakraText,
  useDisclosure,
  VStack,
  Circle,
  Alert,
  AlertDescription,
  AlertIcon,
} from '@chakra-ui/react'
import React, { useContext, useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { GiSpeedometer } from 'react-icons/gi'
import { IoMdSpeedometer } from 'react-icons/io'
import useFetch, { CachePolicies } from 'use-http'
import { colors } from '../../../ui-kit'
import { currencySymbol, getNumber, PermissionsEnum } from '../../../utils'
import {
  CancelSubscriptionModal,
} from '../../components/Billing'
import type {
  SubscriptionDetails,
  SubscriptionDetailsResponse,
  SubscriptionPrice,
} from '../../interfaces'
import { UserContext } from '../../providers'
import { MainContentHeadingBox, MainContentWrapper } from '../Base/MainContent'
import PermissionGuard from '../../components/PermissionGuard'
import { SubscriptionDetals } from '../../components/Billing/Subscription'
import { DiscountedPricingAlertBox } from '../../components/Billing/DiscountedPricing'
import { NavLink } from 'react-router-dom'

const getTieredCostText = ({
  tiers,
  tiers_mode,
  unit,
  currency: currencyCode,
}: SubscriptionPrice & { unit: string }): string[] => {
  const currency = currencySymbol(currencyCode)
  const tierCount = tiers?.length ?? 0

  const tierCostArr: string[] = []

  tiers?.forEach((tier, index) => {
    const costArr: string[] = []
    if (tier.flat_amount) {
      const flatAmount = parseFloat((tier.flat_amount / 100).toFixed(2))
      costArr.push(`${currency}${flatAmount} fixed`)
    }
    if (tier.unit_amount) {
      const unitAmount = parseFloat((tier.unit_amount / 100).toFixed(2))
      costArr.push(`${currency}${unitAmount} per ${unit}`)
    }
    const costStr = costArr.join(' and ')
    const lastTier = tiers[index - 1]

    let prefix = ''
    if (index === tierCount - 1) {
      prefix =
        tiers_mode === 'graduated' ? 'for the rest' : 'if the total quantity is'
      tierCostArr.push(`${prefix} ${lastTier.up_to + 1} or more, ${costStr}`)
    } else if (index === 0) {
      prefix =
        tiers_mode === 'graduated'
          ? 'For the first'
          : 'If the total quantity is'
      tierCostArr.push(`${prefix} 1 to ${tier.up_to}, ${costStr}`)
    } else {
      prefix =
        tiers_mode === 'graduated' ? 'for the next' : 'if the total quantity is'
      tierCostArr.push(
        `${prefix} ${lastTier.up_to + 1} to ${tier.up_to}, ${costStr}`,
      )
    }
  })

  return tierCostArr
}

const SubscriptionsLayout = (): React.ReactElement => {
  const { t } = useTranslation()
  const { user } = useContext(UserContext)

  const { isOpen, onClose, onOpen } = useDisclosure()

  const [subscription, setSubscription] = useState<SubscriptionDetails>()

  const { request, loading } = useFetch<SubscriptionDetailsResponse>({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const getData = async (): Promise<void> => {
    if (user?.clientNumber && user.billingDetails.billTo === 'self') {
      const data = (await request.get(
        `/api/billing/subscription-details/${user.clientNumber}`,
      )) as SubscriptionDetailsResponse | undefined

      if (data && data.metadata && data.items) {
        let {
          contractMonths,
          contractBandwidthQuotaInGB = 0,
          contractVideoProcessingUnitsQuotaInCount = 0,
          contractExtensionUnitsQuotaInCount = 0,
          contractPurgeCacheRequestsInCount = 0,
        } = data.metadata

        const subscriptionDetails: SubscriptionDetails = {
          currencySymbol: currencySymbol(data.currency),
          collectionMethod: {
            method:
              data.collection_method === 'charge_automatically'
                ? 'automatic'
                : 'manual',
            cardDetails: data.card_details,
          },
          isStandardPlan: false,
          contractInfo: {
            hasContract: false,
          },
          bandwidth: {
            label: 'Media delivery bandwidth',
            inclusion: '',
            overage: '',
          },
          storage: {
            label: 'DAM storage',
            inclusion: '',
            overage: '',
          },
          video: {
            label: 'Video processing units',
            inclusion: '',
            maximumOverageUnits: -1,
            overage: '',
          },
          extension: {
            label: 'Extension units',
            inclusion: '',
            overage: '',
            maximumOverageUnits: data.extensionInfo.maximumOverageUnits,
            rateCard: data.extensionInfo.rateCard
          },
          userPrice: {
            label: 'User seats',
            inclusion: '',
            overage: '',
          },
          purgeCacheAPIPrice: {
            label: 'Purge cache requests',
            inclusion: '',
            overage: '',
          },
          CNAMEPrice: {
            label: 'Custom domain names',
            inclusion: '',
            overage: '',
          },
          originalCacheStoragePrice: {
            label: 'Original cache storage',
            inclusion: '',
            overage: '',
          },
          request: {
            label: 'Requests',
            inclusion: '',
            overage: '',
          },
        }

        subscriptionDetails.contractInfo = data.contractInfo;

        data.items.data.forEach(item => {
          const price = item.price
          const currency = currencySymbol(price.currency)
          const cost = parseFloat(
            (parseFloat(price.unit_amount_decimal ?? '0') / 100).toFixed(3),
          )
          if (price.metadata.type === 'basePlan') {
            if (cost && cost > 0) {
              subscriptionDetails.fixedCost = (
                <>
                  <span style={{ fontFamily: "sans-serif", fontWeight: "700" }}>{currency}</span>{cost} / {price.recurring.interval}
                </>
              )

              if (price.currency === "usd" && cost <= 89) {
                subscriptionDetails.isStandardPlan = true
              }

              if (price.currency === "inr" && (cost / 83) <= 89) {
                subscriptionDetails.isStandardPlan = true
              }
            }
          }
          if (price.metadata.type === 'bandwidthOverage') {
            let unit = '1GB'
            if (price.transform_quantity?.divide_by) {
              unit = `${getNumber(price.transform_quantity.divide_by, 10)}GB`
            }
            if (
              price.billing_scheme === 'per_unit' &&
              typeof cost === 'number'
            ) {
              if (cost === 0) {
                subscriptionDetails.bandwidth.overage = "Monthly limit. The limit resets at the beginning of every calendar month."
              } else {
                subscriptionDetails.bandwidth.overage =
                  `${currency}${cost} per ${unit} ` +
                  `/ ${price.recurring.interval}`
              }
            } else if (price.billing_scheme === 'tiered' && price.tiers) {
              subscriptionDetails.bandwidth.overage = getTieredCostText({
                ...price,
                unit,
              }).join(', ')
            }

            if (price.metadata.fixedIncludedBandwidth === '-1') {
              subscriptionDetails.bandwidth.inclusion = t(
                'unlimited_bandwidth',
                'Unlimited bandwidth',
              )
            } else if (price.metadata.fixedIncludedBandwidth) {
              subscriptionDetails.bandwidth.inclusion = `${getNumber(
                price.metadata.fixedIncludedBandwidth,
              )}GB / ${price.recurring.interval} included`
            }
          }
          if (price.metadata.type === 'requestOverage') {
            let unit = '1 million'
            if (price.transform_quantity?.divide_by) {
              unit = `${getNumber(
                price.transform_quantity.divide_by,
                10,
              )} million`
            }
            if (
              price.billing_scheme === 'per_unit' &&
              typeof cost === 'number'
            ) {
              subscriptionDetails.request.overage =
                `${currency}${cost} per ${unit} ` +
                `/ ${price.recurring.interval}`
            } else if (price.billing_scheme === 'tiered' && price.tiers) {
              subscriptionDetails.request.overage = getTieredCostText({
                ...price,
                unit,
              }).join(', ')
            }
            if (price.metadata.fixedIncludedRequests === '-1') {
              subscriptionDetails.request.inclusion = t(
                'unlimited',
                'Unlimited',
              )
            } else if (price.metadata.fixedIncludedRequests) {
              subscriptionDetails.request.inclusion = `${getNumber(
                price.metadata.fixedIncludedRequests,
              )} ${t('million', 'million')} / ${price.recurring.interval} included`
            }
          }
          if (price.metadata.type === 'storageOverage') {
            let unit = '1GB'
            if (price.transform_quantity?.divide_by) {
              unit = `${getNumber(price.transform_quantity.divide_by)}GB`
            }
            if (
              price.billing_scheme === 'per_unit' &&
              typeof cost === 'number'
            ) {
              if (cost === 0) {
                subscriptionDetails.storage.overage = "Fixed limit"
              } else {
                subscriptionDetails.storage.overage = `${currency}${cost} per ${unit} / ${price.recurring.interval}`
              }
            } else if (price.billing_scheme === 'tiered' && price.tiers) {
              subscriptionDetails.storage.overage = getTieredCostText({
                ...price,
                unit,
              }).join(', ')
            }
            if (price.metadata.fixedIncludedStorage === '-1') {
              subscriptionDetails.storage.inclusion = t(
                'unlimited_storage',
                'Unlimited storage for use with ImageKit',
              )
            } else if (price.metadata.fixedIncludedStorage) {
              subscriptionDetails.storage.inclusion = `${getNumber(
                price.metadata.fixedIncludedStorage,
              )}GB included`
            } else if (
              price.metadata.includedStorageToBandwidthRatio &&
              !isNaN(getNumber(price.metadata.includedStorageToBandwidthRatio))
            ) {
              const ratio = getNumber(
                price.metadata.includedStorageToBandwidthRatio,
              )
              if (ratio === 1) {
                subscriptionDetails.storage.inclusion = t(
                  'ratio_1_fixed_storage',
                )
              } else {
                subscriptionDetails.storage.inclusion = t('ratio_storage', {
                  ratio,
                })
              }
            }
          }
          if (price.metadata.type === 'videoProcessingUnitsOverage') {
            let unit = '1'
            if (price.transform_quantity?.divide_by) {
              unit = `${getNumber(price.transform_quantity.divide_by)}`
            }
            if (
              price.billing_scheme === 'per_unit' &&
              typeof cost === 'number'
            ) {
              if (cost === 0) {
                subscriptionDetails.video.overage = "Monthly limit. The limit resets at the beginning of every calendar month."
              } else {
                subscriptionDetails.video.overage =
                  `${currency}${cost} per ${unit} ` +
                  `units / ${price.recurring.interval}`
              }
            } else if (price.billing_scheme === 'tiered' && price.tiers) {
              subscriptionDetails.video.overage =
                getTieredCostText({ ...price, unit }).join(', ')
            }
            if (price.metadata.monthlyInclusion === '-1') {
              subscriptionDetails.video.inclusion = t(
                'unlimited_video_processing_units',
                'Unlimited video processing units',
              )
            } else if (price.metadata.monthlyInclusion) {
              subscriptionDetails.video.inclusion = `${getNumber(
                price.metadata.monthlyInclusion,
              )} units / ${price.recurring.interval} included`
            }
            if (price.metadata.maximumOverageUnits) {
              subscriptionDetails.video.maximumOverageUnits =
                parseInt(price.metadata.maximumOverageUnits, 10) || -1
            }
          }
          if (price.metadata.type === 'extensionUnitsOverage') {
            let unit = '1'
            if (price.transform_quantity?.divide_by) {
              unit = `${getNumber(price.transform_quantity.divide_by)}`
            }
            if (
              price.billing_scheme === 'per_unit' &&
              typeof cost === 'number'
            ) {
              if (cost === 0) {
                subscriptionDetails.extension.overage = "Monthly limit. The limit resets at the beginning of every calendar month."
              } else {
                subscriptionDetails.extension.overage =
                  `${currency}${cost} per ${unit} units ` +
                  `/ ${price.recurring.interval}`
                if (price.metadata.maximumOverageUnits) {
                  subscriptionDetails.extension.overage += `. Maximum ${price.metadata.maximumOverageUnits} units can be consumed in a month.`
                }
              }
            } else if (price.billing_scheme === 'tiered' && price.tiers) {
              subscriptionDetails.extension.overage = getTieredCostText({
                ...price,
                unit,
              }).join(', ')
            }
            if (price.metadata.monthlyInclusion === '-1') {
              subscriptionDetails.extension.inclusion = t(
                'unlimited_extension_units',
                'Unlimited extension units',
              )
            } else if (price.metadata.monthlyInclusion) {
              subscriptionDetails.extension.inclusion = `${parseInt(
                price.metadata.monthlyInclusion,
                10,
              )} units / ${price.recurring.interval} included`
            }
          }

          if (price.metadata.type === 'userSeatsOverage') {
            let unit = '1'
            if (price.transform_quantity?.divide_by) {
              unit = `${getNumber(price.transform_quantity.divide_by)}`
            }
            if (
              price.billing_scheme === 'per_unit' &&
              typeof cost === 'number'
            ) {
              if (cost === 0) {
                subscriptionDetails.userPrice.overage = "Fixed limit"
              } else {
                subscriptionDetails.userPrice.overage = `${currency}${cost} per ${unit} user seat `
              }
            } else if (price.billing_scheme === 'tiered' && price.tiers) {
              subscriptionDetails.userPrice.overage = getTieredCostText({
                ...price,
                unit,
              }).join(', ')
            }
            if (price.metadata.monthlyInclusion === '-1') {
              subscriptionDetails.userPrice.inclusion = t(
                'unlimited_user_seats',
                'Unlimited user seats',
              )
            } else if (price.metadata.monthlyInclusion) {
              subscriptionDetails.userPrice.inclusion = `${parseInt(
                price.metadata.monthlyInclusion,
                10,
              )} included`
            }
          }

          if (price.metadata.type === 'purgeCacheRequestsOverage') {
            let unit = '1'
            if (price.transform_quantity?.divide_by) {
              unit = `${getNumber(price.transform_quantity.divide_by)}`
            }
            if (
              price.billing_scheme === 'per_unit' &&
              typeof cost === 'number'
            ) {
              if (cost === 0) {
                subscriptionDetails.purgeCacheAPIPrice.overage = "Monthly limit. The limit resets at the beginning of every calendar month."
              } else {
                subscriptionDetails.purgeCacheAPIPrice.overage =
                  `${currency}${cost} per ${unit} requests ` +
                  `/ ${price.recurring.interval}`
              }
            } else if (price.billing_scheme === 'tiered' && price.tiers) {
              subscriptionDetails.purgeCacheAPIPrice.overage = getTieredCostText(
                {
                  ...price,
                  unit,
                },
              ).join(', ')
            }
            if (price.metadata.monthlyInclusion === '-1') {
              subscriptionDetails.purgeCacheAPIPrice.inclusion = t(
                'unlimited_purge_cache_requests',
                'Unlimited purge cache requests',
              )
            } else if (price.metadata.monthlyInclusion) {
              subscriptionDetails.purgeCacheAPIPrice.inclusion = `${parseInt(
                price.metadata.monthlyInclusion,
                10,
              )} requests / ${price.recurring.interval} included`
            }
          }

          if (price.metadata.type === 'CNAMEOverage') {
            let unit = '1'
            if (price.transform_quantity?.divide_by) {
              unit = `${getNumber(price.transform_quantity.divide_by)}`
            }
            if (
              price.billing_scheme === 'per_unit' &&
              typeof cost === 'number'
            ) {
              if (cost === 0) {
                subscriptionDetails.CNAMEPrice.overage = "Fixed limit"
              } else {
                subscriptionDetails.CNAMEPrice.overage = `${currency}${cost} per ${unit} custom domain names`
              }
            } else if (price.billing_scheme === 'tiered' && price.tiers) {
              subscriptionDetails.CNAMEPrice.overage = getTieredCostText({
                ...price,
                unit,
              }).join(', ')
            }
            if (price.metadata.monthlyInclusion === '-1') {
              subscriptionDetails.CNAMEPrice.inclusion = t(
                'unlimited_cnames',
                'Unlimited CNAMEs',
              )
            } else if (price.metadata.monthlyInclusion) {
              subscriptionDetails.CNAMEPrice.inclusion = `${parseInt(
                price.metadata.monthlyInclusion,
                10,
              )} included`
            }
          }

          if (price.metadata.type === 'originalCacheStorageOverage') {
            let unit = '1GB'
            if (price.transform_quantity?.divide_by) {
              unit = `${getNumber(price.transform_quantity.divide_by)}GB`
            }
            if (
              price.billing_scheme === 'per_unit' &&
              typeof cost === 'number'
            ) {
              if (cost === 0) {
                subscriptionDetails.originalCacheStoragePrice.overage = "Fixed limit"
              } else {
                subscriptionDetails.originalCacheStoragePrice.overage = `${currency}${cost} per ${unit} / ${price.recurring.interval}`
              }
            } else if (price.billing_scheme === 'tiered' && price.tiers) {
              subscriptionDetails.originalCacheStoragePrice.overage =
                getTieredCostText({
                  ...price,
                  unit,
                }).join(', ')
            }
            if (price.metadata.monthlyInclusion === '-1') {
              subscriptionDetails.originalCacheStoragePrice.inclusion = t(
                'unlimited_original_cache_storage',
                'Unlimited original cache storage for use with ImageKit',
              )
            } else if (price.metadata.monthlyInclusion) {
              subscriptionDetails.originalCacheStoragePrice.inclusion = `${getNumber(
                price.metadata.monthlyInclusion,
              )}GB included`
            }
          }
        })

        const inclusions: string[] = []

        const { contractInfo } = subscriptionDetails

        if (contractInfo.hasContract) {
          inclusions.push(
            `${contractInfo.contractMonths} ${t(
              'months_contract_includes',
              'months contract includes',
            )}:`,
          )

          if (contractInfo.contractBandwidthQuotaInGB) {
            inclusions.push(
              `${contractInfo.contractBandwidthQuotaInGB}GB ${t(
                'bandwidth',
                'bandwidth',
              )}`,
            )
            subscriptionDetails.bandwidth.inclusion = `${contractInfo.contractBandwidthQuotaInGB}GB for ${contractInfo.contractMonths} months contract included`
          }

          if (contractInfo.contractVideoProcessingUnitsQuotaInCount) {
            inclusions.push(
              `${contractInfo.contractVideoProcessingUnitsQuotaInCount} ${t(
                'video_processing_units',
                'video processing units',
              )}`,
            )
            subscriptionDetails.video.inclusion = `${contractInfo.contractVideoProcessingUnitsQuotaInCount} units for ${contractInfo.contractMonths} months contract included`
          }

          if (contractInfo.contractExtensionUnitsQuotaInCount) {
            inclusions.push(
              `${contractInfo.contractExtensionUnitsQuotaInCount} ${t(
                'extension_units',
                'extension units',
              )}`,
            )
            subscriptionDetails.extension.inclusion = `${contractInfo.contractExtensionUnitsQuotaInCount} units for ${contractInfo.contractMonths} months contract included`
          }

          if (contractInfo.contractPurgeCacheRequestsInCount) {
            inclusions.push(
              `${contractInfo.contractPurgeCacheRequestsInCount} ${t(
                'purge_cache_requests',
                'Purge cache requests',
              )}`,
            )
            subscriptionDetails.purgeCacheAPIPrice.inclusion = `${contractInfo.contractPurgeCacheRequestsInCount} requests for ${contractInfo.contractMonths} months contract included`
          }
        }

        setSubscription(subscriptionDetails)
      }
    }
  }

  useEffect(() => {
    void getData()
  }, [user])

  if (user?.billingDetails.billTo !== 'self') {
    return (
      <>
        <MainContentHeadingBox />
        <MainContentWrapper>
          <Alert status="info">
            <AlertIcon />
            <AlertDescription>
              {t(
                'billing_managed_by_parent_description',
                "This account's billing is managed by the parent agency account. For support kindly contact us at support@imagekit.io.",
              )}
            </AlertDescription>
          </Alert>
        </MainContentWrapper>
      </>
    )
  }

  return (
    <>
      <MainContentHeadingBox>
        <Heading
          as="h5"
          size="md"
          fontWeight="600"
          color={colors.appDarkIndigo}
          lineHeight="3rem"
        >
          &nbsp;
        </Heading>
        <PermissionGuard permission={PermissionsEnum.SELF_BILLING_MANAGE}>
          {(hasPermission: boolean) =>
            hasPermission ? (
              <HStack spacing="1em">
                <Button variant="deleteCTA" as={NavLink} to="/change-plan">
                  {t('change_plan', 'Change plan or cancel subscription')}
                </Button>
              </HStack>
            ) : (
              <></>
            )
          }
        </PermissionGuard>
      </MainContentHeadingBox>
      <Box padding="1.5em">
        <VStack
          px={8}
          py={6}
          justifyContent="flex-start"
          alignItems="flex-start"
          spacing="1em"
        >
          <DiscountedPricingAlertBox />
          {!loading && subscription ? (
            <SubscriptionDetals subscription={subscription} />
          ) : null}
          {!loading && !subscription ? (
            <ChakraText marginBottom="1em" color={colors.appSlate}>
              {t(
                'error_loading_subscription_details',
                'Error in loading subscription details. Please contact support.',
              )}
            </ChakraText>
          ) : null}
          {loading ? (
            <ChakraText marginBottom="1em" color={colors.appSlate}>
              {t('loading', 'Loading...')}
            </ChakraText>
          ) : null}
        </VStack>
      </Box>
    </>
  )
}

export default SubscriptionsLayout
